import { getSafely } from 'shared/web';
import type { ActionDefinition, ActionDefinitionObject } from '../types/ActionDefinition';
import type { RowActions } from '../types/RowData';
import { classifyRowAction } from './classifyRowAction';
export type RowActionItem = {
  actionKey: string;
  definition: ActionDefinition;
};
export type RowActionsResult = {
  /** Actions with icon — rendered as IconButton in action column */
  iconActions: RowActionItem[];
  /** Actions without icon — rendered as menu items in overflow dropdown */
  overflowActions: RowActionItem[];
};

/**
 * Pure utility — not a hook. Partitions visible row actions into icon and overflow groups.
 *
 * - Includes only actions where `singleAction === true` in definition
 * - Per-row primary/secondary arrays determine which actions are visible
 * - Partitions by icon presence:
 *   - Has icon → iconActions (rendered as IconButton — includes ROW_CLICK with icon)
 *   - No icon + SECONDARY → overflowActions (rendered in DropdownAction overflow menu)
 *   - No icon + ROW_CLICK → excluded (only fires on row click, no button rendered)
 */
export function partitionRowActions(rowActions: RowActions, actionsDefinition: ActionDefinitionObject): RowActionsResult {
  const allKeys = [...rowActions.primary, ...rowActions.secondary];
  const iconActions: RowActionItem[] = [];
  const overflowActions: RowActionItem[] = [];
  for (const key of allKeys) {
    const definition = getSafely(actionsDefinition, key);
    if (!definition || !definition.singleAction) {
      continue;
    }
    const slot = classifyRowAction(definition);
    if (slot === 'icon') {
      iconActions.push({
        actionKey: key,
        definition
      });
    } else if (slot === 'overflow') {
      overflowActions.push({
        actionKey: key,
        definition
      });
    }
    // 'excluded' → skip silently
  }
  return {
    iconActions,
    overflowActions
  };
}